import type { GameState } from '../game/GameState.js';
import type { Color, Move } from '../game/types.js';
import type { Difficulty } from '../settings/types.js';
import type { DifficultyStrategy, MoveChoice } from './DifficultyStrategy.js';

export interface AiPlayerOptions {
  readonly color: Color;
  readonly strategy: DifficultyStrategy;
  /**
   * Minimum delay between "your turn" and the move being emitted, in ms.
   * Search runs synchronously (it's fast enough for our depths); the delay
   * just keeps the AI from feeling jittery on Easy/Normal where decisions
   * are essentially instant.
   */
  readonly minThinkMs: number;
  readonly onMove: (move: Move, choice: MoveChoice) => void;
}

/**
 * Orchestrates the AI side. The host (`main.ts`) tells the player when it is
 * the AI's turn; the player computes a move via `DifficultyStrategy`, waits
 * out the minimum think delay, and emits the move via `onMove`. The host then
 * runs the existing `PieceAnimator` plan against the returned `Move` — the
 * chain (path + captures + mid-jump promotion) is already encoded inside the
 * move, so no per-step animation orchestration is needed here.
 */
export class AiPlayer {
  private difficulty: Difficulty = 'normal';
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;
  private generation = 0;

  constructor(private readonly opts: AiPlayerOptions) {}

  setDifficulty(difficulty: Difficulty): void {
    this.difficulty = difficulty;
  }

  get color(): Color {
    return this.opts.color;
  }

  /** Drop any in-flight think-delay timer — call on reset / navigation. */
  cancel(): void {
    this.generation += 1;
    if (this.pendingTimer !== null) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
  }

  /**
   * Notify the AI it's their move. Idempotent: if a pending move is already
   * scheduled for this color, this call is a no-op (the host shouldn't call
   * twice anyway, but we defend against it).
   */
  notifyTurn(state: GameState): void {
    if (state.currentPlayer !== this.opts.color) return;
    if (this.pendingTimer !== null) return;

    const startMs = performance.now();
    const choice = this.opts.strategy.pickMove(state, this.difficulty);
    if (!choice) return; // No legal moves — the host's status check should already have ended the game.

    const elapsed = performance.now() - startMs;
    const wait = Math.max(0, this.opts.minThinkMs - elapsed);
    const generation = this.generation;
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      if (generation !== this.generation) return; // cancelled
      this.opts.onMove(choice.move, choice);
    }, wait);
  }
}
